import { motion } from "framer-motion";
import { ArrowRight, Users, Sprout, Mountain, Heart, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";

const Home = () => {
  const stats = [
    { number: "2000+", label: "Farmers Engaged", icon: Users },
    { number: "50+", label: "Villages Covered", icon: Mountain },
    { number: "6+", label: "Years of Impact", icon: Heart },
    { number: "100%", label: "Organic Focus", icon: Sprout },
  ];

  const partners = [
    "M3M Foundation",
    "SPJIMR DOCC",
    "H2O Not on Map",
    "HimERA Network",
    "Holistic Himalayas",
    "Himalaya Bachao Samiti",
    "NABARD",
    "Krishi Vigyan Kendra",
  ];

  const programmes = [
    {
      title: "Sustainable Agriculture & Livelihood",
      description: "Promoting climate-resilient natural farming and organic practices across Chamba district.",
      icon: Sprout,
    },
    {
      title: "Traditional Seed Revival",
      description: "Preserving indigenous biodiversity through seed banks and the Chamba Beej Khojo Abhiyan.",
      icon: Mountain,
    },
    {
      title: "Community Empowerment",
      description: "Building resilient women-led groups and strengthening local governance.",
      icon: Users,
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1727417014832-2ac00fa0d77d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxjYWxtJTIwaGltYWxheWFzJTIwbW91bnRhaW4lMjBsYW5kc2NhcGUlMjBkaGFyYW1zaGFsYXxlbnwwfHx8fDE3NzQ3NjM2OTd8MA&ixlib=rb-4.1.0&q=85')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-semibold text-white leading-none mb-6"
              style={{ textShadow: "0 2px 10px rgba(0,0,0,0.3)" }}
            >
              Empowering Himalayan Communities
            </h1>
            <p className="text-lg sm:text-xl text-white/90 mb-8 leading-relaxed max-w-2xl mx-auto"
              style={{ textShadow: "0 1px 5px rgba(0,0,0,0.3)" }}
            >
              Building resilient, self-reliant, and ecologically sustainable communities through climate-resilient agriculture and traditional knowledge systems
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/about"
                data-testid="hero-learn-more-btn"
                className="bg-[#2B5C45] text-white hover:bg-[#214835] rounded-full px-8 py-4 font-medium transition-all shadow-lg inline-flex items-center justify-center"
              >
                Learn More
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/donate"
                data-testid="hero-donate-btn"
                className="bg-[#C86A4C] text-white hover:bg-[#B35A3D] rounded-full px-8 py-4 font-medium transition-all shadow-lg inline-flex items-center justify-center"
              >
                Support Our Work
                <Heart className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 1 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <ChevronDown className="h-8 w-8 text-white animate-bounce" />
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <stat.icon className="h-10 w-10 text-[#2B5C45] mx-auto mb-4" />
                <h3 className="text-3xl sm:text-4xl font-heading font-semibold text-[#1C221F] mb-2">
                  {stat.number}
                </h3>
                <p className="text-sm text-[#515E58]">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-24 bg-[#F9F8F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-medium text-[#1C221F] mb-6">
                About PAHAD Trust
              </h2>
              <p className="text-base leading-relaxed text-[#515E58] mb-6">
                PAHAD Trust (Professional Assistance for Hill Area Development) is a grassroots, public charitable organization established in 2018 in Himachal Pradesh with a vision to strengthen sustainable and inclusive development in the Himalayan region.
              </p>
              <p className="text-base leading-relaxed text-[#515E58] mb-8">
                We work at the intersection of climate-resilient agriculture, sustainable livelihoods, traditional knowledge systems, and participatory local governance, enabling communities to lead their own development processes.
              </p>
              <Link
                to="/about"
                data-testid="about-read-more-btn"
                className="bg-transparent border border-[#2B5C45] text-[#2B5C45] hover:bg-[#F9F8F6] rounded-full px-6 py-3 font-medium transition-all inline-flex items-center"
              >
                Read More
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative h-96 rounded-2xl overflow-hidden shadow-lg"
            >
              <img
                src="https://images.unsplash.com/photo-1708513520903-f96f6cd9310d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBmYXJtZXJzJTIwc3VzdGFpbmFibGUlMjBhZ3JpY3VsdHVyZSUyMG9yZ2FuaWN8ZW58MHx8fHwxNzc0NzYzNjk4fDA&ixlib=rb-4.1.0&q=85"
                alt="PAHAD Trust community work"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Programmes */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-medium text-[#1C221F] mb-4">
              Our Key Programmes
            </h2>
            <p className="text-base text-[#515E58] max-w-2xl mx-auto">
              Transforming lives through sustainable initiatives
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {programmes.map((programme, index) => (
              <motion.div
                key={programme.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#F9F8F6] rounded-xl p-8 shadow-sm border border-[#E4E2DC]/40 card-hover"
              >
                <programme.icon className="h-12 w-12 text-[#2B5C45] mb-4" />
                <h3 className="text-xl sm:text-2xl font-heading font-medium text-[#1C221F] mb-3">
                  {programme.title}
                </h3>
                <p className="text-base text-[#515E58] leading-relaxed">
                  {programme.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PAHAD Organics */}
      <section className="py-24 bg-[#F0EFEB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative h-96 rounded-2xl overflow-hidden shadow-lg order-2 lg:order-1"
            >
              <img
                src="https://images.unsplash.com/photo-1708417134307-3ce870f8a31a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjBmYXJtZXJzJTIwc3VzdGFpbmFibGUlMjBhZ3JpY3VsdHVyZSUyMG9yZ2FuaWN8ZW58MHx8fHwxNzc0NzYzNjk4fDA&ixlib=rb-4.1.0&q=85"
                alt="PAHAD Organics"
                className="w-full h-full object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-medium text-[#C86A4C] mb-6">
                PAHAD Organics
              </h2>
              <p className="text-base leading-relaxed text-[#515E58] mb-6">
                A livelihood enterprise supporting farmers in marketing their organic and natural produce, strengthening local value chains and improving rural incomes.
              </p>
              <p className="text-base leading-relaxed text-[#515E58] mb-8">
                Straight from the farms of Himachal Pradesh to your table - pure, natural, and sustainable.
              </p>
              <div className="flex items-center space-x-4">
                <Sprout className="h-8 w-8 text-[#2B5C45]" />
                <div>
                  <p className="font-medium text-[#1C221F]">100% Organic</p>
                  <p className="text-sm text-[#515E58]">Chemical-free farming</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-16 bg-[#8C9A74]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl sm:text-3xl font-heading font-medium text-center text-[#1C221F] mb-12"
          >
            Our Partners & Collaborators
          </motion.h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="flex justify-center"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_pahad-organics/artifacts/c5tjfr84_WhatsApp%20Image%202026-03-29%20at%2009.24.18.jpeg"
                alt="M3M Foundation"
                className="h-24 w-auto object-contain grayscale hover:grayscale-0 transition-all"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="flex justify-center"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_pahad-organics/artifacts/6ny1zhh0_WhatsApp%20Image%202026-03-29%20at%2009.24.23.jpeg"
                alt="SPJIMR DOCC"
                className="h-24 w-auto object-contain grayscale hover:grayscale-0 transition-all"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex justify-center"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_pahad-organics/artifacts/h31n51w4_WhatsApp%20Image%202026-03-29%20at%2009.24.19.jpeg"
                alt="NotOnMap"
                className="h-24 w-auto object-contain grayscale hover:grayscale-0 transition-all"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="flex justify-center"
            >
              <div className="text-center">
                <p className="text-lg font-medium text-[#2B5C45]">NABARD</p>
                <p className="text-sm text-[#515E58]">& More</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#2B5C45]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-medium text-white mb-6">
              Join Us in Making a Difference
            </h2>
            <p className="text-lg text-white/90 mb-8 leading-relaxed">
              Support sustainable development in the Himalayan region
            </p>
            <Link
              to="/donate"
              data-testid="cta-donate-btn"
              className="bg-white text-[#2B5C45] hover:bg-[#F9F8F6] rounded-full px-8 py-4 font-medium transition-all shadow-lg inline-flex items-center justify-center"
            >
              Donate Now
              <Heart className="ml-2 h-5 w-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
